import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { TrendingUp, FileText, Anchor } from 'lucide-react';

// --------- HELPERS ----------
const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

function computeSafeTooltipPosition({ barRect, containerRect, tipW, tipH, prefAbove = true }) {
  const PADDING = 8;
  const OFFSET  = 10;

  // centro da barra relativo ao container
  let x = (barRect.left - containerRect.left) + (barRect.width / 2);

  const yTop    = (barRect.top - containerRect.top) - OFFSET - tipH; // acima
  const yBottom = (barRect.bottom - containerRect.top) + OFFSET;      // abaixo

  let placeAbove = prefAbove;
  let y = placeAbove ? yTop : yBottom;

  // se acima não cabe, tenta abaixo
  if (placeAbove && y < PADDING) {
    placeAbove = false;
    y = yBottom;
  }

  const containerH = containerRect.height;
  // se abaixo não cabe, volta acima
  if (!placeAbove && (y + tipH + PADDING > containerH)) {
    placeAbove = true;
    y = yTop;
  }

  // clamp final
  y = clamp(y, PADDING, Math.max(PADDING, containerH - tipH - PADDING));

  // como usamos translateX(-50%), clamp considerando meia-largura
  const minCenter = PADDING + tipW / 2;
  const maxCenter = containerRect.width - PADDING - tipW / 2;
  x = clamp(x, minCenter, maxCenter);

  return { x, y, placeAbove };
}
// ----------------------------

export default function InvestmentChart() {
  const [animatedData, setAnimatedData] = useState([]);
  const [hoveredBar, setHoveredBar]     = useState(null);
  const [tooltipData, setTooltipData]   = useState(null);   // {year, value}
  const [tooltipPos, setTooltipPos]     = useState(null);   // {x, y, placeAbove}
  const [isMobile, setIsMobile]         = useState(false);

  // Refs
  const scrollRef       = useRef(null);       // wrapper com overflow-x no mobile
  const innerRefDesktop = useRef(null);       // container RELATIVE (desktop)
  const innerRefMobile  = useRef(null);       // container RELATIVE (mobile)
  const tooltipRef      = useRef(null);       // mede tamanho do tooltip

  const investmentData = [
    { year: 2010, value: 7074 }, { year: 2011, value: 3883 }, { year: 2012, value: 2570 },
    { year: 2013, value: 5052 }, { year: 2014, value: 5587 }, { year: 2015, value: 3250 },
    { year: 2016, value: 1828 }, { year: 2017, value: 1033 }, { year: 2018, value: 1101 },
    { year: 2019, value: 875  }, { year: 2020, value: 536  }, { year: 2021, value: 480  },
    { year: 2022, value: 340  }, { year: 2023, value: 150  }, { year: 2024, value: 266  }
  ];

  const CHART_HEIGHT = 320; // px
  const maxValue = Math.max(...investmentData.map(d => d.value));
  const getBarHeight = (value) => (value / maxValue) * (CHART_HEIGHT - 20);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setAnimatedData(investmentData), 300);
    return () => clearTimeout(t);
  }, []);

  const getColor = (year, isHovered) => {
    const base =
      year === 2024 ? 'bg-green-500' :
      year >= 2020   ? 'bg-red-400'   :
      year >= 2015   ? 'bg-yellow-400': 'bg-blue-500';
    return `${base} ${isHovered ? 'opacity-90' : ''}`;
  };

  // Tooltip com posicionamento seguro (duplo RAF para iOS/Safari)
  const showTooltip = (item, index, targetEl) => {
    const container = (isMobile ? innerRefMobile : innerRefDesktop).current;
    if (!container) return;

    const barRect       = targetEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    setHoveredBar(index);
    setTooltipData({ year: item.year, value: item.value });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const tip = tooltipRef.current;
        const tipW = tip ? tip.offsetWidth  : 140;
        const tipH = tip ? tip.offsetHeight : 44;

        const { x, y, placeAbove } = computeSafeTooltipPosition({
          barRect,
          containerRect,
          tipW,
          tipH,
          prefAbove: !isMobile // mobile: abaixo; desktop: acima
        });

        setTooltipPos({ x, y, placeAbove });
      });
    });
  };

  const handleEnter = (item, index) => (e) => showTooltip(item, index, e.currentTarget);
  const handleLeave = () => { setHoveredBar(null); setTooltipData(null); setTooltipPos(null); };

  // Pointer Events unificados (mouse + touch)
  const getBarHandlers = (item, index) => ({
    onPointerDown:  (e) => handleEnter(item, index)(e),              // tap/click
    onPointerEnter: (e) => !isMobile && handleEnter(item, index)(e), // hover desktop
    onPointerMove:  (e) => !isMobile && handleEnter(item, index)(e),
    onPointerLeave: handleLeave,
  });

  // medidas mobile
  const BAR_W_MOBILE      = 18;
  const GAP_MOBILE        = 10;
  const SLOT_W_MOBILE     = BAR_W_MOBILE + GAP_MOBILE;
  const contentWidthMobile= investmentData.length * SLOT_W_MOBILE + 16;
  const touchSafeStyle    = { touchAction: 'manipulation', userSelect: 'none' };

  return (
    <div className="space-y-8">
      <Card className="w-full">
        <CardContent className="p-6">
          {isMobile ? (
            // --- MOBILE: scroll horizontal + container relativo ---
            <div className="overflow-x-auto no-scrollbar" ref={scrollRef} onScroll={handleLeave}>
              <div
                className="relative flex items-end px-4"
                ref={innerRefMobile}
                style={{ height: CHART_HEIGHT, width: contentWidthMobile }}
              >
                {investmentData.map((item, index) => (
                  <div key={item.year} className="flex flex-col items-center justify-end" style={{ width: SLOT_W_MOBILE }}>
                    <div
                      className={`${getColor(item.year, hoveredBar === index)} rounded-t transition-all duration-300 cursor-pointer`}
                      style={{
                        ...touchSafeStyle,
                        width: BAR_W_MOBILE,
                        height: `${animatedData[index] ? getBarHeight(item.value) : 0}px`,
                        transitionDelay: `${index * 40}ms`,
                        transform: hoveredBar === index ? 'translateY(-2px)' : 'translateY(0)'
                      }}
                      {...getBarHandlers(item, index)}
                    />
                    <span className="text-[10px] mt-1 text-gray-500">{String(item.year).slice(2)}</span>
                  </div>
                ))}

                {tooltipData && (
                  <div
                    ref={tooltipRef}
                    className="absolute z-10 bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg whitespace-nowrap pointer-events-none"
                    style={{
                      left: tooltipPos ? tooltipPos.x : -9999,
                      top: tooltipPos ? tooltipPos.y : -9999,
                      transform: tooltipPos?.placeAbove ? 'translate(-50%,-10%)' : 'translate(-50%,10%)'
                    }}
                  >
                    <div className="text-sm font-semibold">{tooltipData.year}</div>
                    <div className="text-xs">R$ {tooltipData.value.toLocaleString('pt-BR')} M</div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            // --- DESKTOP ---
            <div className="h-80 px-4 relative" ref={innerRefDesktop}>
              <div className="flex items-end justify-between h-full gap-1">
                {investmentData.map((item, index) => (
                  <div key={item.year} className="flex flex-col items-center justify-end h-full">
                    <div
                      className={`w-10 rounded-t transition-all duration-300 cursor-pointer ${getColor(item.year, hoveredBar === index)} hover:opacity-80`}
                      style={{
                        ...touchSafeStyle,
                        height: `${animatedData[index] ? getBarHeight(item.value) : 0}px`,
                        transitionDelay: `${index * 50}ms`,
                        transform: hoveredBar === index ? 'translateY(-2px)' : 'translateY(0)'
                      }}
                      {...getBarHandlers(item, index)}
                    />
                    <span className="text-xs mt-2 text-gray-500">{item.year}</span>
                  </div>
                ))}
              </div>

              {tooltipData && (
                <div
                  ref={tooltipRef}
                  className="absolute z-10 bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg whitespace-nowrap pointer-events-none"
                  style={{
                    left: tooltipPos ? tooltipPos.x : -9999,
                    top: tooltipPos ? tooltipPos.y : -9999,
                    transform: tooltipPos?.placeAbove ? 'translate(-50%,-10%)' : 'translate(-50%,10%)'
                  }}
                >
                  <div className="text-sm font-semibold">{tooltipData.year}</div>
                  <div className="text-xs">R$ {tooltipData.value.toLocaleString('pt-BR')} M</div>
                </div>
              )}
            </div>
          )}

          {/* Legenda */}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="flex items-center gap-2"><div className="w-4 h-4 bg-blue-500 rounded" /> <span>2010–2014</span></div>
            <div className="flex items-center gap-2"><div className="w-4 h-4 bg-yellow-400 rounded" /> <span>2015–2019</span></div>
            <div className="flex items-center gap-2"><div className="w-4 h-4 bg-red-400 rounded" /> <span>2020–2023</span></div>
            <div className="flex items-center gap-2"><div className="w-4 h-4 bg-green-500 rounded" /> <span>2024</span></div>
          </div>
        </CardContent>
      </Card>

      {/* Box informativo 2024 (inalterado) */}
      <Card className="bg-gradient-to-br from-blue-50 to-indigo-100">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-blue-900">Aplicação dos Investimentos em 2024</CardTitle>
        </CardHeader>
        <CardContent className="grid md:grid-cols-3 gap-4">
          <div className="p-4 bg-white/60 rounded-lg">
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-blue-600 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-blue-800">FIOL (Oeste-Leste)</h4>
                <p className="text-sm text-gray-700">86% do total foi para o trecho II da ferrovia (Caetité–Barreiras/BA).</p>
              </div>
            </div>
          </div>
          <div className="p-4 bg-white/60 rounded-lg">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-green-600 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-green-800">Ramal de Barra Mansa (RJ)</h4>
                <p className="text-sm text-gray-700">Mais de R$ 18 milhões investidos na adequação do ramal.</p>
              </div>
            </div>
          </div>
          <div className="p-4 bg-white/60 rounded-lg">
            <div className="flex items-center gap-3">
              <Anchor className="w-8 h-8 text-purple-600 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-purple-800">FICO (Centro-Oeste)</h4>
                <p className="text-sm text-gray-700">Mais de R$ 8 milhões em aportes para o trecho Mara Rosa (GO) a Porto Velho (RO).</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
